import { useEffect, useMemo, useState } from "react";
import {
    GetAllSetting,
    GetSettingById,
    UpdateSetting,
} from "../services/SettingService";
import { useLanguage } from "./LanguageContext"; // مسیر واقعی خودت

/* -------------------------------------------------------------------------- */
/*  متن‌ها                                                                     */
/* -------------------------------------------------------------------------- */
const TEXTS = {
    fa: {
        dir: "rtl",
        title: "مدیریت تنظیمات",
        subtitle: "لیست تنظیمات سیستم را مشاهده و مدیریت کنید",
        searchPlaceholder: "جستجو با کلید یا مقدار تنظیمات...",
        edit: "ویرایش",
        key: "کلید",
        value: "مقدار",
        modifiedDate: "تاریخ ویرایش",
        save: "ذخیره",
        cancel: "انصراف",
        modalEditTitle: "ویرایش تنظیمات",
        loading: "در حال بارگذاری...",
        saving: "در حال ذخیره...",
        empty: "هنوز هیچ تنظیماتی ثبت نشده است",
        noSearchResult: "نتیجه‌ای یافت نشد",
        toastUpdateSuccess: "تنظیمات با موفقیت ویرایش شد",
        toastUpdateFail: "خطا در ویرایش تنظیمات",
        toastFetchFail: "خطا در دریافت لیست تنظیمات",
        itemsCount: (n) => `${n} تنظیمات`,
        viewCard: "کارت",
        viewList: "فهرست",
        actions: "عملیات",
        noValue: "—",
        showingRange: (from, to, total) => `نمایش ${from} تا ${to} از ${total} تنظیمات`,
        itemsPerPage: "تعداد در هر صفحه",
        prevPage: "قبلی",
        nextPage: "بعدی",
    },
    ar: {
        dir: "rtl",
        title: "إدارة الإعدادات",
        subtitle: "عرض وإدارة إعدادات النظام",
        searchPlaceholder: "بحث بالمفتاح أو القيمة...",
        edit: "تعديل",
        key: "المفتاح",
        value: "القيمة",
        modifiedDate: "تاريخ التعديل",
        save: "حفظ",
        cancel: "إلغاء",
        modalEditTitle: "تعديل الإعداد",
        loading: "جارٍ التحميل...",
        saving: "جارٍ الحفظ...",
        empty: "لم يتم تسجيل أي إعداد بعد",
        noSearchResult: "لا توجد نتائج",
        toastUpdateSuccess: "تم تعديل الإعداد بنجاح",
        toastUpdateFail: "خطأ في تعديل الإعداد",
        toastFetchFail: "خطأ في جلب قائمة الإعدادات",
        itemsCount: (n) => `${n} إعداد`,
        viewCard: "بطاقة",
        viewList: "قائمة",
        actions: "إجراءات",
        noValue: "—",
        showingRange: (from, to, total) => `عرض ${from} إلى ${to} من ${total} إعداد`,
        itemsPerPage: "عدد العناصر في الصفحة",
        prevPage: "السابق",
        nextPage: "التالي",
    },
};

/* -------------------------------------------------------------------------- */
/*  آیکن‌ها                                                                    */
/* -------------------------------------------------------------------------- */
function Icon({ name, size = 14, color = "currentColor" }) {
    const paths = {
        plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
        edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
        trash: <><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></>,
        search: <><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></>,
        close: <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>,
        sliders: <><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></>,
        check: <polyline points="20 6 9 17 4 12" />,
        alert: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>,
        grid: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /></>,
        list: <><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></>,
    };
    return (
        <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
            fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
            {paths[name] ?? null}
        </svg>
    );
}

/* -------------------------------------------------------------------------- */
/*  Toast (بالای صفحه)                                                        */
/* -------------------------------------------------------------------------- */
function Toast({ message, visible, type }) {
    if (!visible) return null;
    const isError = type === "error";
    return (
        <div
            className={`fixed top-5 left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-xl px-5 py-2.5 text-xs font-medium text-white z-[3000] animate-[toastDrop_.25s_ease_both] ${isError ? "bg-[#EF4444] shadow-[0_8px_24px_rgba(239,68,68,.3)]" : "bg-[#10B981] shadow-[0_8px_24px_rgba(16,185,129,.3)]"
                }`}
        >
            <Icon name={isError ? "alert" : "check"} size={13} color="#fff" />
            {message}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  فیلدهای فرم                                                                */
/* -------------------------------------------------------------------------- */
const FIELD_BASE =
    "w-full h-10 border-[1.5px] border-[#E8ECF4] rounded-[10px] px-3 text-xs bg-[#F8FAFF] outline-none text-[#1E293B] box-border transition-all duration-150 focus:bg-white focus:border-[#6C3DFF] focus:shadow-[0_0_0_4px_rgba(108,61,255,.08)]";
const FIELD_ERROR =
    "border-[#EF4444] bg-[#FEF2F2] focus:border-[#EF4444] focus:shadow-[0_0_0_4px_rgba(239,68,68,.08)]";

function TextField({ value, onChange, placeholder, autoFocus, hasError, disabled, inputMode }) {
    return (
        <input
            autoFocus={autoFocus}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            inputMode={inputMode}
            className={`${FIELD_BASE} ${hasError ? FIELD_ERROR : ""} ${disabled ? "cursor-not-allowed bg-[#F1F5F9] text-[#94A3B8]" : ""}`}
        />
    );
}

/* -------------------------------------------------------------------------- */
/*  نرمال‌سازی داده‌ها (منطبق بر Entity/ViewModel واقعی)                        */
/*  اگر پاسخ سرور PascalCase برگرداند (Key, Value, ...) هم پوشش داده شده        */
/* -------------------------------------------------------------------------- */
const normalizeSetting = (raw) => ({
    id: raw.id ?? raw.Id,
    key: raw.key ?? raw.Key ?? "",
    value: raw.value ?? raw.Value ?? "",
    modifiedDate: raw.modifiedDate ?? raw.ModifiedDate ?? null,
    isDelete: raw.isDelete ?? raw.IsDelete ?? false,
});

function getInitials(key) {
    const trimmed = (key || "").trim();
    return trimmed ? trimmed.charAt(0).toUpperCase() : "؟";
}

/* -------------------------------------------------------------------------- */
/*  برچسب خوانا برای کلیدهای شناخته‌شده‌ی تنظیمات (فقط برای نمایش، نه ذخیره‌سازی) */
/* -------------------------------------------------------------------------- */
const KEY_LABELS = {
    fa: {
        LowOrderCost: "کمترین مبلغ فاکتور",
        LowOrderWeight: "کمترین وزن فاکتور",
    },
    ar: {
        LowOrderCost: "أقل مبلغ للفاتورة",
        LowOrderWeight: "أقل وزن للفاتورة",
    },
};

function getKeyLabel(key, lang) {
    const map = KEY_LABELS[lang] || KEY_LABELS.fa;
    return map[key] || key;
}

/* -------------------------------------------------------------------------- */
/*  واحد مقدار برای کلیدهای شناخته‌شده (فقط برای نمایش)                        */
/* -------------------------------------------------------------------------- */
const UNIT_LABELS = {
    fa: {
        LowOrderCost: "دینار",
        LowOrderWeight: "کیلوگرم",
    },
    ar: {
        LowOrderCost: "دينار",
        LowOrderWeight: "كيلوغرام",
    },
};

function getUnitLabel(key, lang) {
    const map = UNIT_LABELS[lang] || UNIT_LABELS.fa;
    return map[key] || "";
}

/* -------------------------------------------------------------------------- */
/*  کلیدهایی که مقدارشون باید فقط عددی باشه                                    */
/* -------------------------------------------------------------------------- */
const NUMERIC_ONLY_KEYS = ["LowOrderCost", "LowOrderWeight"];

function getDisplayValue(setting, lang, t) {
    if (!setting.value) return t.noValue;
    const displayNumber = NUMERIC_ONLY_KEYS.includes(setting.key)
        ? formatNumberWithSeparators(setting.value)
        : setting.value;
    return `${displayNumber} ${getUnitLabel(setting.key, lang)}`.trim();
}

function sanitizeNumericInput(raw) {
    // فقط رقم و یک نقطه اعشار مجازه؛ هر کاراکتر دیگه‌ای حذف میشه
    let val = (raw || "").replace(/[^\d.]/g, "");
    const parts = val.split(".");
    if (parts.length > 2) {
        val = parts[0] + "." + parts.slice(1).join("");
    }
    return val;
}

function formatNumberWithSeparators(value) {
    if (value === null || value === undefined || value === "") return "";
    const str = String(value);
    const isNegative = str.startsWith("-");
    const unsigned = isNegative ? str.slice(1) : str;
    const [intPart, decPart] = unsigned.split(".");
    const groupedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return (isNegative ? "-" : "") + groupedInt + (decPart !== undefined ? "." + decPart : "");
}

function formatDate(value) {
    if (!value) return "—";
    try {
        const d = new Date(value);
        // مقدار پیش‌فرض DateTime در دات‌نت (0001-01-01) یعنی هنوز ویرایش نشده؛ آن را تاریخ معتبر حساب نمی‌کنیم
        if (isNaN(d.getTime()) || d.getFullYear() <= 1) return "—";
        return d.toLocaleDateString("fa-IR");
    } catch {
        return "—";
    }
}

/* -------------------------------------------------------------------------- */
/*  کارت تنظیمات                                                              */
/* -------------------------------------------------------------------------- */
function SettingCard({ setting, onEdit, t, lang }) {
    return (
        <div className="flex flex-col gap-3 rounded-2xl border border-[#EEF2FF] bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.03)] transition-all duration-[180ms] hover:-translate-y-0.5 hover:border-[#E4DBFF] hover:shadow-[0_8px_24px_rgba(108,61,255,0.10)]">
            <div className="flex items-start gap-2.5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#6C3DFF1A] to-[#9B59FF33] text-[15px] font-bold text-[#6C3DFF]">
                    {getInitials(getKeyLabel(setting.key, lang))}
                </div>
                <div className="min-w-0 flex-1">
                    <p className="m-0 truncate text-[13px] font-bold text-[#1E293B]">
                        {getKeyLabel(setting.key, lang)}
                    </p>
                    <div className="mt-[3px] text-[10.5px] text-[#94A3B8]">
                        {formatDate(setting.modifiedDate)}
                    </div>
                </div>
            </div>

            <div className="truncate rounded-[10px] border border-[#EEF2FF] bg-[#F8FAFF] px-2.5 py-2 text-xs font-semibold text-[#475569]">
                {getDisplayValue(setting, lang, t)}
            </div>

            <div className="mt-1 flex gap-2">
                <button
                    onClick={() => onEdit(setting)}
                    className="flex h-[34px] flex-1 items-center justify-center gap-1.5 rounded-[9px] border border-[#E8ECF4] bg-white text-[11px] font-semibold text-[#1E293B] cursor-pointer"
                >
                    <Icon name="edit" size={12} color="#6C3DFF" />
                    {t.edit}
                </button>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  نمای فهرستی (جدول) تنظیمات                                                */
/* -------------------------------------------------------------------------- */
const LIST_GRID_COLS = "grid-cols-[1fr_1.4fr_130px_60px]";

function SettingListView({ settings, onEdit, t, lang }) {
    const headers = [t.key, t.value, t.modifiedDate, t.actions];
    return (
        <div className="overflow-x-auto rounded-2xl border border-[#EEF2FF] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.03)]">
            <div className="min-w-[620px]">
                <div className={`sticky top-0 z-[1] grid ${LIST_GRID_COLS} border-b border-[#F1F5F9] bg-[#F8FAFF] px-4 py-3`}>
                    {headers.map((h, i) => (
                        <span key={i} className="whitespace-nowrap text-[10.5px] font-bold text-[#94A3B8]">{h}</span>
                    ))}
                </div>

                <div className="max-h-[560px] overflow-y-auto">
                    {settings.map((setting, i) => (
                        <div
                            key={setting.id}
                            className={`grid ${LIST_GRID_COLS} items-center border-b border-[#F5F7FF] px-4 py-3 transition-colors duration-150 hover:bg-[#FAFAFF] ${i % 2 === 1 ? "bg-[#EEF1F7]" : "bg-white"
                                }`}
                        >
                            <span className="truncate text-xs font-bold text-[#1E293B]">
                                {getKeyLabel(setting.key, lang)}
                            </span>

                            <span className="truncate text-[11.5px] text-[#64748B]">
                                {getDisplayValue(setting, lang, t)}
                            </span>

                            <span className="whitespace-nowrap text-[11px] text-[#94A3B8]">
                                {formatDate(setting.modifiedDate)}
                            </span>

                            <div className="flex gap-1.5">
                                <button
                                    onClick={() => onEdit(setting)}
                                    title={t.edit}
                                    className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-[#E8ECF4] bg-white cursor-pointer"
                                >
                                    <Icon name="edit" size={13} color="#6C3DFF" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  تاگل نمای کارت / فهرست                                                     */
/* -------------------------------------------------------------------------- */
function ViewToggle({ mode, onChange, t }) {
    const options = [
        { key: "list", label: t.viewList, icon: "list" },
        { key: "card", label: t.viewCard, icon: "grid" },
    ];
    return (
        <div className="flex gap-0.5 rounded-[10px] bg-[#F1F5F9] p-[3px]">
            {options.map((opt) => {
                const active = mode === opt.key;
                return (
                    <button
                        key={opt.key}
                        type="button"
                        onClick={() => onChange(opt.key)}
                        className={`flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg border-none px-3 text-[11px] font-semibold transition-all duration-150 cursor-pointer ${active
                                ? "bg-white text-[#6C3DFF] shadow-[0_1px_4px_rgba(15,23,42,0.08)]"
                                : "bg-transparent text-[#94A3B8]"
                            }`}
                    >
                        <Icon name={opt.icon} size={13} color={active ? "#6C3DFF" : "#94A3B8"} />
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  پیجینیشن                                                                   */
/* -------------------------------------------------------------------------- */
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function Pagination({ page, pageSize, total, onPageChange, onPageSizeChange, t }) {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const endItem = Math.min(page * pageSize, total);

    const getPageNumbers = () => {
        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
            return pages;
        }
        pages.push(1);
        if (page > 3) pages.push("…");
        for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
            pages.push(i);
        }
        if (page < totalPages - 2) pages.push("…");
        pages.push(totalPages);
        return pages;
    };

    return (
        <div className="mt-3.5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#EEF2FF] bg-white px-[18px] py-3.5 shadow-[0_1px_3px_rgba(0,0,0,0.03)]">
            {/* راست: متن نمایش + انتخاب تعداد در صفحه */}
            <div className="flex flex-wrap items-center gap-3.5">
                <span className="whitespace-nowrap text-[11px] text-[#94A3B8]">
                    {t.showingRange(startItem, endItem, total)}
                </span>

                <div className="flex items-center gap-2">
                    <span className="whitespace-nowrap text-[11px] text-[#94A3B8]">{t.itemsPerPage}</span>
                    <select
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        className="h-8 cursor-pointer rounded-[9px] border-[1.5px] border-[#E8ECF4] bg-[#F8FAFF] px-2.5 text-[11.5px] font-semibold text-[#1E293B] outline-none"
                    >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* چپ: قبلی / شماره صفحات / بعدی */}
            <div className="flex flex-wrap items-center gap-1.5">
                <button
                    type="button"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    className={`h-8 rounded-[9px] border-[1.5px] border-[#E8ECF4] bg-white px-3.5 text-[11px] font-semibold transition-all duration-150 ${page <= 1 ? "cursor-not-allowed text-[#CBD5E1]" : "cursor-pointer text-[#475569]"
                        }`}
                >
                    {t.prevPage}
                </button>

                {getPageNumbers().map((p, i) =>
                    p === "…" ? (
                        <span key={`dots-${i}`} className="px-0.5 text-[11px] text-[#94A3B8]">
                            …
                        </span>
                    ) : (
                        <button
                            type="button"
                            key={p}
                            onClick={() => onPageChange(p)}
                            className={`h-8 w-8 cursor-pointer rounded-[9px] border-none text-[11.5px] font-bold transition-all duration-150 ${p === page ? "bg-[#6C3DFF] text-white" : "bg-transparent text-[#475569]"
                                }`}
                        >
                            {p}
                        </button>
                    )
                )}

                <button
                    type="button"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages}
                    className={`h-8 rounded-[9px] border-[1.5px] border-[#E8ECF4] bg-white px-3.5 text-[11px] font-semibold transition-all duration-150 ${page >= totalPages ? "cursor-not-allowed text-[#CBD5E1]" : "cursor-pointer text-[#475569]"
                        }`}
                >
                    {t.nextPage}
                </button>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  کامپوننت اصلی                                                              */
/* -------------------------------------------------------------------------- */
export default function SettingManagement() {
    const { lang } = useLanguage();
    const t = TEXTS[lang] || TEXTS.fa;

    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState({ visible: false, message: "", type: "success" });
    const [searchTerm, setSearchTerm] = useState("");
    const [viewMode, setViewMode] = useState("list"); // card | list — پیش‌فرض: فهرست
    const [modal, setModal] = useState(null); // {loadingData, data:{id, key, value}}
    const [saving, setSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const showToast = (message, type = "success") => {
        setToast({ visible: true, message, type });
        setTimeout(() => setToast({ visible: false, message: "", type: "success" }), 3000);
    };

    const extractServerErrorMessage = (err) => {
        const data = err?.response?.data;
        if (typeof data === "string" && data.trim()) return data.trim();
        if (data && typeof data === "object") {
            return data.message || data.title || data.error || null;
        }
        return null;
    };

    const fetchSettings = async () => {
        try {
            const res = await GetAllSetting();
            const raw = res.data?.data ?? res.data ?? [];
            setSettings(Array.isArray(raw) ? raw.map(normalizeSetting) : []);
        } catch (err) {
            showToast(extractServerErrorMessage(err) || t.toastFetchFail, "error");
        }
    };

    useEffect(() => {
        (async () => {
            setLoading(true);
            await fetchSettings();
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = useMemo(() => {
        let list = settings;
        if (searchTerm.trim()) {
            const lower = searchTerm.trim().toLowerCase();
            list = list.filter(
                (s) =>
                    (s.key || "").toLowerCase().includes(lower) ||
                    (s.value || "").toLowerCase().includes(lower)
            );
        }
        return list;
    }, [settings, searchTerm]);

    // بازگشت به صفحه اول هنگام تغییر جستجو یا تعداد آیتم در هر صفحه
    useEffect(() => {
        setPage(1);
    }, [searchTerm, pageSize]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

    // اگر صفحه فعلی از تعداد کل صفحات بیشتر شد (مثلا بعد از حذف)، به آخرین صفحه معتبر برگرد
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const paginated = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    }, [filtered, page, pageSize]);

    const openEditModal = async (setting) => {
        setFieldErrors({});
        setModal({ loadingData: true, data: { ...setting } });
        try {
            const res = await GetSettingById(setting.id);
            const raw = res.data?.data ?? res.data;
            if (raw) {
                const fresh = normalizeSetting(raw);
                setModal({ loadingData: false, data: { ...fresh } });
            } else {
                setModal((m) => (m ? { ...m, loadingData: false } : m));
            }
        } catch (err) {
            setModal(null); // مودال رو کلاً می‌بندیم چون داده‌ای برای نمایش نداریم
            showToast(extractServerErrorMessage(err) || t.toastFetchFail, "error");
        }
    };

    const closeModal = () => { if (!saving) setModal(null); };

    const handleSubmit = async () => {
        if (!modal) return;
        setFieldErrors({});

        // کلید قابل ویرایش نیست؛ همان مقدار ثابت به همراه مقدار جدید ارسال می‌شود
        const payload = {
            id: modal.data.id,
            key: modal.data.key,
            value: modal.data.value?.trim() || null,
        };

        setSaving(true);
        try {
            await UpdateSetting(payload);
            showToast(t.toastUpdateSuccess);
            setModal(null);
            await fetchSettings();
        } catch (err) {
            console.error("خطای ذخیره تنظیمات:", err, err?.response?.data);
            showToast(extractServerErrorMessage(err) || t.toastUpdateFail, "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div dir={t.dir} className="min-h-screen bg-[#F5F6FA] font-[IRANSansWeb,Vazirmatn,sans-serif]">
            <style>{`
                @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
                @keyframes toastDrop { from { opacity:0; transform:translate(-50%,-12px); } to { opacity:1; transform:translate(-50%,0); } }
            `}</style>

            {/* هدر */}
            <div className="relative overflow-hidden bg-gradient-to-br from-[#6C3DFF] to-[#9B59FF] px-7 pt-[26px] pb-[84px]">
                <div className="absolute -left-[60px] -top-[60px] h-[220px] w-[220px] rounded-full bg-white/[0.06]" />
                <div className="absolute -bottom-10 right-20 h-40 w-40 rounded-full bg-white/[0.04]" />

                <div className="relative z-[1] flex items-center justify-between gap-3">
                    <div>
                        <h1 className="m-0 text-[19px] font-bold text-white">{t.title}</h1>
                        <p className="mb-0 mt-1 text-[11px] text-white/65">{t.subtitle}</p>
                    </div>
                </div>
            </div>

            {/* باکس جستجو + نمای فهرست/کارت */}
            <div className="relative z-[2] -mt-14 animate-[fadeUp_.4s_ease_both] px-7">
                <div className="overflow-hidden rounded-[18px] border border-[#EEF2FF] bg-white shadow-[0_8px_28px_rgba(108,61,255,0.10)]">
                    <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                        <div className="relative min-w-[200px] flex-1">
                            <input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder={t.searchPlaceholder}
                                className={`${FIELD_BASE} pl-9 pr-3`}
                            />
                            <span className="absolute left-3 top-1/2 -translate-y-1/2">
                                <Icon name="search" size={13} color="#94A3B8" />
                            </span>
                        </div>

                        <span className="whitespace-nowrap text-[10px] text-[#94A3B8]">{t.itemsCount(filtered.length)}</span>

                        <ViewToggle mode={viewMode} onChange={setViewMode} t={t} />
                    </div>
                </div>
            </div>

            {/* گرید / جدول تنظیمات */}
            <div className="animate-[fadeUp_.5s_.1s_ease_both] px-7 pb-10 pt-[18px]">
                {loading ? (
                    <div className="flex flex-col items-center py-14">
                        <div className="mb-3 h-9 w-9 animate-spin rounded-full border-[3px] border-[#EDE9FF] border-t-[#6C3DFF]" />
                        <p className="text-xs text-[#94A3B8]">{t.loading}</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 rounded-[18px] border border-[#EEF2FF] bg-white py-[46px]">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#6C3DFF14]">
                            <Icon name="sliders" size={20} color="#6C3DFF" />
                        </div>
                        <p className="m-0 text-xs text-[#94A3B8]">
                            {searchTerm.trim() ? t.noSearchResult : t.empty}
                        </p>
                    </div>
                ) : (
                    <>
                        {viewMode === "card" ? (
                            <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-3.5">
                                {paginated.map((setting) => (
                                    <SettingCard
                                        key={setting.id}
                                        setting={setting}
                                        onEdit={openEditModal}
                                        t={t}
                                        lang={lang}
                                    />
                                ))}
                            </div>
                        ) : (
                            <SettingListView
                                settings={paginated}
                                onEdit={openEditModal}
                                t={t}
                                lang={lang}
                            />
                        )}

                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            total={filtered.length}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            t={t}
                        />
                    </>
                )}
            </div>

            {/* مودال افزودن / ویرایش تنظیمات */}
            {modal && (
                <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-[rgba(15,23,42,.45)] p-4">
                    <div className="max-h-[90vh] w-[min(420px,100%)] overflow-y-auto rounded-[18px] bg-white shadow-[0_20px_60px_rgba(15,23,42,.25)]">
                        <div className="sticky top-0 z-[1] flex items-center justify-between border-b border-[#F1F5F9] bg-white px-5 py-4">
                            <div className="flex items-center gap-2">
                                <span className="flex h-[26px] w-[26px] items-center justify-center rounded-lg bg-[#6C3DFF14]">
                                    <Icon name="sliders" size={12} color="#6C3DFF" />
                                </span>
                                <p className="m-0 text-[13px] font-bold text-[#1E293B]">
                                    {t.modalEditTitle}
                                </p>
                            </div>
                            <button onClick={closeModal} className="cursor-pointer border-none bg-transparent p-1">
                                <Icon name="close" size={16} color="#94A3B8" />
                            </button>
                        </div>

                        <div className="flex flex-col gap-3 p-5">
                            {modal.loadingData ? (
                                <div className="flex justify-center py-[18px]">
                                    <div className="h-[26px] w-[26px] animate-spin rounded-full border-[3px] border-[#EDE9FF] border-t-[#6C3DFF]" />
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <label className="mb-1.5 block text-[10px] font-semibold text-[#475569]">{t.key}</label>
                                        <TextField
                                            value={getKeyLabel(modal.data.key, lang)}
                                            disabled
                                            placeholder={t.key}
                                        />
                                    </div>

                                    <div>
                                        <label className="mb-1.5 block text-[10px] font-semibold text-[#475569]">
                                            {t.value}
                                            {getUnitLabel(modal.data.key, lang) && (
                                                <span className="mr-1 font-normal text-[#94A3B8]">
                                                    ({getUnitLabel(modal.data.key, lang)})
                                                </span>
                                            )}
                                        </label>
                                        <TextField
                                            autoFocus
                                            value={
                                                NUMERIC_ONLY_KEYS.includes(modal.data.key)
                                                    ? formatNumberWithSeparators(modal.data.value)
                                                    : modal.data.value
                                            }
                                            onChange={(e) => {
                                                const isNumeric = NUMERIC_ONLY_KEYS.includes(modal.data.key);
                                                const rawInput = isNumeric
                                                    ? e.target.value.replace(/,/g, "")
                                                    : e.target.value;
                                                const nextValue = isNumeric
                                                    ? sanitizeNumericInput(rawInput)
                                                    : rawInput;
                                                setModal((m) => ({ ...m, data: { ...m.data, value: nextValue } }));
                                            }}
                                            placeholder={t.value}
                                            inputMode={NUMERIC_ONLY_KEYS.includes(modal.data.key) ? "decimal" : "text"}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="sticky bottom-0 flex justify-end gap-2 border-t border-[#F1F5F9] bg-white px-5 py-3.5">
                            <button
                                onClick={closeModal}
                                disabled={saving}
                                className="h-[38px] cursor-pointer rounded-[10px] border-[1.5px] border-[#E8ECF4] bg-white px-4 text-[11.5px] font-semibold text-[#475569]"
                            >
                                {t.cancel}
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={saving || modal.loadingData}
                                className={`flex h-[38px] items-center gap-2 rounded-[10px] border-none px-5 text-[11.5px] font-semibold text-white ${saving ? "cursor-not-allowed bg-[#A78BFA]" : "cursor-pointer bg-[#6C3DFF]"
                                    }`}
                            >
                                {saving && <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                                {saving ? t.saving : t.save}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <Toast message={toast.message} visible={toast.visible} type={toast.type} />
        </div>
    );
}